import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Flame, Search, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const ALL = "All";

export default function TestimoniesArchive() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(ALL);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/testimonies/featured?limit=50")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((t) => t.category || "General"));
    return [ALL, ...Array.from(set).sort()];
  }, [items]);

  const visible = items.filter((t) => {
    const passCat = filter === ALL || (t.category || "General") === filter;
    const ql = q.trim().toLowerCase();
    const passQ = !ql ||
      (t.testimony || "").toLowerCase().includes(ql) ||
      (t.first_name || "").toLowerCase().includes(ql) ||
      (t.category || "").toLowerCase().includes(ql);
    return passCat && passQ;
  });

  return (
    <div data-testid="testimonies-archive-page" className="min-h-screen pt-24 pb-24">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#ff5c00]/8 via-transparent to-transparent" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-4">
            Testimonies · UGYC Dubai
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter font-bold leading-[0.95] mb-6">
            What God <br className="hidden md:block" />
            <span className="text-[#ff5c00]">Has Done</span>
          </h1>
          <p className="max-w-2xl text-zinc-300 text-lg leading-relaxed">
            Real stories from our community — healings, breakthroughs, restored families,
            answered prayers. Read them. Be encouraged. Share one of your own.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/testimony"
              data-testid="archive-share-cta"
              className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs tracking-[0.2em] uppercase px-6 py-3 rounded-sm transition-colors"
            >
              Share Your Testimony <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-white/5 bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="archive-search"
              placeholder="Search by word, name, or category…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-[#0a0a0d] border border-white/10 focus:border-[#ff5c00] outline-none rounded-sm pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`archive-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`font-mono-accent text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-sm border transition-colors ${
                  filter === c
                    ? "bg-[#ff5c00]/10 text-white border-[#ff5c00]/40"
                    : "text-zinc-400 border-white/10 hover:text-white hover:border-white/30"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        {loading ? (
          <p className="text-center text-zinc-500 font-mono-accent text-xs uppercase tracking-widest py-20">Loading…</p>
        ) : visible.length === 0 ? (
          <div data-testid="archive-empty" className="text-center py-20">
            <Sparkles className="w-8 h-8 text-[#ff5c00]/40 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">No testimonies found here yet.</p>
            <Link
              to="/testimony"
              className="font-mono-accent text-xs tracking-[0.2em] uppercase text-[#ff5c00] hover:text-white"
            >
              Be the first to share →
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((t, i) => (
              <motion.article
                key={t.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                data-testid={`archive-card-${i}`}
                className="relative bg-[#0f0f14] border border-white/10 hover:border-[#ff5c00]/40 rounded-sm p-6 transition-colors"
              >
                {t.featured && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#ff5c00]">
                    <Sparkles className="w-2.5 h-2.5" /> Featured
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  {t.image_url ? (
                    <img
                      src={t.image_url}
                      alt={t.first_name}
                      className="w-11 h-11 rounded-full object-cover border border-white/10"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#ff5c00]/10 border border-[#ff5c00]/20 flex items-center justify-center">
                      <Flame className="w-4 h-4 text-[#ff5c00]" />
                    </div>
                  )}
                  <div>
                    <p className="font-display text-base uppercase tracking-tight text-white">
                      {t.first_name}{t.last_initial ? ` ${t.last_initial}.` : ""}
                    </p>
                    <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#ff5c00] mt-0.5">
                      {t.category}
                    </p>
                  </div>
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap line-clamp-[10]">
                  {t.testimony}
                </p>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
